import { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode, Navigation, Thumbs, Pagination } from "swiper";

import { handleDetailInfoModalOpened, handleSetActiveImageIndex } from "@/src/store/listingDetail";

import { Ga4WithEventParameters } from "src/helpers/google-tag-manager/events/ga4-with-eventparameters";
import { convertToResizedUrl } from "@/src/helpers/project-image-resize";

import PhotoIcon from "assets/svg/thumb_list_photo.svg";
import VideoIcon from "assets/svg/thumb_list_video.svg";
import VirtualTourIcon from "assets/svg/thumb_list_3d.svg";

import Container from "@/src/components/grid/container";
import PageTitle from "@/src/components/project-detail/page-title";
import ListItem from "components/listing-detail/photo-thumbnails/list-item";
import SwiperWrapper from "components/swiper-wrapper";
import { LazyImage } from "components/common/lazy-image/LazyImage";
import MapView from "components/project-detail/map-view";
import ImageThumbnail from "components/project-detail/image-thumbnail";
import PriceDetail from "components/project-detail/price-detail";

import "swiper/css";
import "swiper/css/navigation";

import styles from "./styles.module.css";

const ProjectHero = ({
  name,
  companyName,
  currency,
  price,
  images,
  videoUrl,
  virtualTourUrl,
  updateFormattedDataImages,
}) => {
  const dispatch = useDispatch();
  const swiperRef = useRef(null);

  const handleClickSwiper = (event) => {
    const clickedElement = event.target;
    if (["swiper-button-prev", "swiper-button-next"].includes(clickedElement.className)) {
      Ga4WithEventParameters({
        eventName: "click_button",
        eventParameters: {
          label: `galeri||${clickedElement.className === "swiper-button-prev" ? "prev" : "next"}`,
        },
      });
    }
  };

  const handleKeyboardTrigger = (event) => {
    const direction = event?.key;

    if (direction === "ArrowLeft") {
      swiperRef.current?.swiper?.slidePrev();
    } else if (direction === "ArrowRight") {
      swiperRef.current?.swiper?.slideNext();
    }
  };

  useEffect(() => {
    window.addEventListener("click", handleClickSwiper);
    window.addEventListener("keydown", handleKeyboardTrigger, false);

    return () => {
      window.removeEventListener("click", handleClickSwiper);
      window.removeEventListener("keydown", handleKeyboardTrigger);
    };
  }, []);

  const handleOpenDetailInfoModal = (activeTabName, activeImageIndex) => {
    updateFormattedDataImages("project-hero");
    Ga4WithEventParameters({
      eventName: activeTabName === "click_widget",
      eventParameters: {
        label:
          activeTabName === "map"
            ? "view||konum"
            : activeTabName === "picture"
            ? "galeri||fotoğraf"
            : activeTabName === "video"
            ? "galeri||video"
            : "galeri||3d",
      },
    });
    dispatch(handleDetailInfoModalOpened(activeTabName));
    if (activeTabName === "picture") setActiveImageIndex(activeImageIndex);
  };

  const setActiveImageIndex = (activeImageIndex) => dispatch(handleSetActiveImageIndex(activeImageIndex));

  const resizedImages = images?.map((image) => convertToResizedUrl(image, 728, 430)) || [];

  return (
    <div className={styles.wrapper}>
      <Container>
        <div className={styles.heroWrapper}>
          <PageTitle title={name} subtitle={companyName} />
          <div className={styles.coverWrap}>
            {images?.length === 0 ? (
              <div className={styles.imageWrapper}>
                <img src="/assets/images/no_photo_big.jpg" className={styles.image} />
              </div>
            ) : images?.length === 1 ? (
              <div className={styles.imageWrapper}>
                <img src={resizedImages[0]} alt={images[0]} className={styles.image} />
              </div>
            ) : (
              <SwiperWrapper>
                <Swiper
                  ref={swiperRef}
                  navigation
                  slidesPerView={"auto"}
                  loop
                  className={styles.sliderWrapper}
                  modules={[FreeMode, Navigation, Thumbs, Pagination]}
                >
                  <div className={styles.slideWrapper}>
                    {images?.map((image, index) => (
                      <SwiperSlide key={index} onClick={() => handleOpenDetailInfoModal("picture", index)}>
                        <div className={styles.imageWrapper}>
                          {index !== 0 ? (
                            <LazyImage src={resizedImages[index]} alt={image} className={styles.image} />
                          ) : (
                            <img src={resizedImages[index]} alt={image} className={styles.image} />
                          )}
                        </div>
                      </SwiperSlide>
                    ))}
                  </div>
                </Swiper>
              </SwiperWrapper>
            )}
            <div className={styles.propertiesList}>
              <ul>
                {images?.length > 0 && (
                  <ListItem
                    icon={<PhotoIcon />}
                    label={`${images.length} Fotoğraf`}
                    handleClick={() => handleOpenDetailInfoModal("picture", 0)}
                  />
                )}
                {videoUrl && (
                  <ListItem icon={<VideoIcon />} label="Video" handleClick={() => handleOpenDetailInfoModal("video")} />
                )}
                {virtualTourUrl && (
                  <ListItem
                    icon={<VirtualTourIcon />}
                    label="3D Tur"
                    handleClick={() => handleOpenDetailInfoModal("three-d")}
                  />
                )}
              </ul>
            </div>
          </div>
          <div className={styles.mapWithImages}>
            {(images?.length >= 2 || videoUrl) && (
              <ImageThumbnail
                alt={name}
                type={videoUrl ? "video" : "picture"}
                imageUrl={resizedImages[1] || resizedImages[0]}
                onClick={() => handleOpenDetailInfoModal(videoUrl ? "video" : "picture", 1)}
              />
            )}
            {(images?.length >= 3 || virtualTourUrl) && (
              <ImageThumbnail
                alt={name}
                type={virtualTourUrl ? "3d" : "picture"}
                imageUrl={resizedImages[2] || resizedImages[1] || resizedImages[0]}
                onClick={() => handleOpenDetailInfoModal(virtualTourUrl ? "three-d" : "picture", 2)}
              />
            )}
            <MapView title={name} onClick={() => handleOpenDetailInfoModal("map")} />
          </div>
          <PriceDetail currency={currency} price={price} />
        </div>
      </Container>
    </div>
  );
};

ProjectHero.propTypes = {
  name: PropTypes.string.isRequired,
  companyName: PropTypes.string,
  currency: PropTypes.string,
  price: PropTypes.number,
  images: PropTypes.array,
  videoUrl: PropTypes.string,
  virtualTourUrl: PropTypes.string,
  updateFormattedDataImages: PropTypes.func,
};

export default ProjectHero;
